import { Box, CircularProgress, Text, VStack } from "@chakra-ui/react";
import { parse } from "date-fns";
import { useMemo, useState } from "react";
import { P, match } from "ts-pattern";
import { useDashboardData } from "../../services/projectService";
import { DashboardData } from "../../types/dashboardData";
import { useDashboardStorageData } from "../../types/dashboardForm";
import { matSitesFromDashboard } from "../../types/map";
import { printWidth } from "../../types/printData";
import { ProjectID } from "../../types/project";
import { DashboardMapView } from "../DashboardMapView/DashboardMapView";
import { GenericDoubleBarGraph } from "../GenericDoubleBarGraph/GenericDoubleBarGraph";

export function GenericPrintGraphsView({ project }: { project: ProjectID }) {
  const [dashboardData] = useDashboardStorageData(project);

  const { startDate, endDate, sites, analyte } = dashboardData;

  if (sites.length === 0 || analyte == null || !startDate || !endDate) {
    return <div>Something went wrong</div>;
  }

  return (
    <GraphsViewContainer
      sites={sites}
      analyte={analyte}
      startDate={parse(startDate, "yyyy-MM-dd", new Date())}
      endDate={parse(endDate, "yyyy-MM-dd", new Date())}
      project={project}
    />
  );
}

function GraphsViewContainer({
  analyte,
  sites,
  startDate,
  endDate,
  project,
}: {
  analyte: string;
  sites: string[];
  startDate: Date;
  endDate: Date;
  project: ProjectID;
}) {
  const query = useDashboardData({ analyte, sites, startDate, endDate, project });

  return match(query)
    .with({ isLoading: true }, () => <CircularProgress isIndeterminate color="brand.400" />)
    .with({ isError: true }, () => <Text color="red">Error loading data</Text>)
    .with({ data: P.nullish }, () => <></>)
    .otherwise(({ data }) => <GraphsView dashboardData={data} sites={sites} analyte={analyte} project={project} />);
}

function GraphsView({
  dashboardData,
  sites,
  analyte,
  project,
}: {
  dashboardData: DashboardData;
  sites: string[];
  analyte: string;
  project: ProjectID;
}) {
  const MapView = useMemo(
    () => <DashboardMapView mapSites={matSitesFromDashboard(dashboardData)} colorContext={project} />,
    [dashboardData, project],
  );
  const [showReferenceLine, setShowReferenceLine] = useState(true);

  return (
    <VStack>
      <Box flex={1} width={printWidth}>
        {MapView}
      </Box>
      <Box flex={1} max-width={printWidth}>
        <GenericDoubleBarGraph
          {...{
            dashboardData,
            sites,
            analyte,
            showReferenceLine,
            setShowReferenceLine,
            colorContext: project,
          }}
        />
      </Box>
    </VStack>
  );
}
